
import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Row, Col, Spinner } from 'react-bootstrap';
import { BsChevronLeft, BsChevronRight } from 'react-icons/bs';
import PatientDetails from './PatientDetails';
import * as apicall from '../../components/Apicall';
import { toast } from 'react-toastify';

const BookAppointment = ({ doctor, onClose, show, serviceShortName, serviceName }) => {
  const today = new Date();
  today.setHours(0, 0, 0, 0); // Normalize today's date
  
  const formatDateLocal = (date) => {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const todayFormatted = formatDateLocal(today);
  
  const [selectedDate, setSelectedDate] = useState(todayFormatted);
  const [selectedSlots, setSelectedSlots] = useState([]);
  const [step, setStep] = useState('calendar');
  const [currentMonth, setCurrentMonth] = useState(today.getMonth());
  const [currentYear, setCurrentYear] = useState(today.getFullYear());
  const [fullDate, setFullDate] = useState(today.toISOString().split('T')[0]);
  const [isLoadingSlots, setIsLoadingSlots] = useState(false);
  const [morningSlots, setMorningSlots] = useState([]);
  const [eveningSlots, setEveningSlots] = useState([]);
  const [unavailableDates, setUnavailableDates] = useState([]);

  const daysInMonth = new Date(currentYear, currentMonth + 1, 0).getDate();
  const firstDayOfMonth = new Date(currentYear, currentMonth, 1).getDay();

  const days = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  // Generate calendar grid
  const calendar = [];
  let day = 1;
  for (let i = 0; i < 6; i++) {
    const week = [];
    for (let j = 0; j < 7; j++) {
      if (i === 0 && j < firstDayOfMonth) {
        week.push(null);
      } else if (day > daysInMonth) {
        week.push(null);
      } else {
        week.push(day);
        day++;
      }
    }
    calendar.push(week);
  }

  useEffect(() => {
    if (doctor && selectedDate) {
      fetchDoctorAvailability(doctor?.id, selectedDate);
      fetchDoctorHolidays(doctor?.id);
    }
  }, [doctor, selectedDate]);

  const getDatesInRange = (startDate, endDate) => {
    const dates = [];
    const getDatePart = (dateStr) => dateStr.split(' ')[0];
    const start = new Date(`${getDatePart(startDate)}T00:00:00Z`);
    const end = new Date(`${getDatePart(endDate)}T00:00:00Z`);
    const current = new Date(start);

    while (current <= end) {
      const dateStr = current.toISOString().split('T')[0];
      dates.push(dateStr);
      current.setUTCDate(current.getUTCDate() + 1);
    }

    return dates;
  };

  const fetchDoctorHolidays = async (doctorId) => {
    try {
      const response = await apicall.get(`get_holidays?user_id=${doctorId}`);
      if (response.status === 200 && response.data.length > 0) {
        const allHolidayDates = response.data.flatMap(holiday =>
          getDatesInRange(holiday.holiday_start, holiday.holiday_end)
        );
        const uniqueUnavailableDates = [...new Set(allHolidayDates)];
        setUnavailableDates(uniqueUnavailableDates);
      } else {
        setUnavailableDates([]);
      }
    } catch (error) {
      console.error('Error fetching doctor holidays:', error);
      toast.error('Failed to fetch doctor holidays');
    }
  };

  const fetchDoctorAvailability = async (doctorId, selectedDate) => {
    try {
      setIsLoadingSlots(true);
      setMorningSlots([]);
      setEveningSlots([]);
      const formattedDate = new Date(selectedDate).toISOString().split('T')[0];
      const response = await apicall.get(`get_available_slots/${doctorId}/${formattedDate}`);
      
      if (response?.data) {
        const { available_slots } = response.data;
        const morning = [];
        const evening = [];

        available_slots.forEach(slot => {
          const startTimeStr = slot.split(' - ')[0].trim();
          const [time, meridian] = startTimeStr.split(' ');
          const hour = parseInt(time.split(':')[0]);

          if (meridian === 'AM' || (meridian === 'PM' && hour < 1)) {
            morning.push(startTimeStr);
          } else {
            evening.push(startTimeStr);
          }
        });

        setMorningSlots(morning);
        setEveningSlots(evening);
      } else {
        setMorningSlots([]);
        setEveningSlots([]);
      }
    } catch (error) {
      console.error('Error fetching doctor availability:', error);
      setMorningSlots([]);
      setEveningSlots([]);
    } finally {
      setIsLoadingSlots(false);
    }
  };

  const handlePrevMonth = () => {
    setCurrentMonth(prev => (prev === 0 ? 11 : prev - 1));
    if (currentMonth === 0) {
      setCurrentYear(prev => prev - 1);
    }
  };

  const handleNextMonth = () => {
    setCurrentMonth(prev => (prev === 11 ? 0 : prev + 1));
    if (currentMonth === 11) {
      setCurrentYear(prev => prev + 1);
    }
  };

  const handleDateClick = (day) => {
    if (day) {
      setSelectedSlots([]);
      setFullDate(day);
      setSelectedDate(day);
    }
  };

  const handleSlotClick = (slot) => {
    setSelectedSlots(prev => {
      if (prev.includes(slot)) {
        return prev.filter(s => s !== slot);
      }

      const allAvailableSlots = [...morningSlots, ...eveningSlots];
      const sortedSelection = [...prev, slot].sort(
        (a, b) => allAvailableSlots.indexOf(a) - allAvailableSlots.indexOf(b)
      );

      // Check for continuous selection
      let isValid = true;
      for (let i = 1; i < sortedSelection.length; i++) {
        if (allAvailableSlots.indexOf(sortedSelection[i]) !==
            allAvailableSlots.indexOf(sortedSelection[i - 1]) + 1) {
          isValid = false;
          break;
        }
      }

      return isValid ? sortedSelection : [slot];
    });
  };

  const handleNext = () => {
    if (unavailableDates.includes(selectedDate)) return;
    if (selectedSlots.length === 0) {
      toast.warn('Please select at least one time slot');
      return;
    }
    setStep('details');
  };

  const handleBack = () => {
    setStep('calendar');
  };

  const handleSubmit = async (values, req_body) => {
    try {
      const response = await apicall.post(req_body, 'book-appointment');
      if (response.status === 200) {
        toast.success('Appointment booked successfully', {
          autoClose: 100,
          onClose: () => onClose()
        });
      }
    } catch (error) {
      console.error('Error creating availability:', error);
      toast.error(error?.message || 'Failed to book appointment');
    }
  };

  const isDateUnavailable = (day) => {
    return unavailableDates.includes(day);
  };

  if (!doctor) return null;

  return (
    <Offcanvas show={show} onHide={onClose} placement="end" style={{ width: '400px' }}>
      <Offcanvas.Header closeButton className="border-bottom">
        <Offcanvas.Title>
          {step === 'calendar' ? 'Select Appointment Date' : 'Book Appointment'}
        </Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body className="p-4">
        {step === 'calendar' ? (
          <>
            <div className="mb-4 text-muted" style={{ fontSize: '14px' }}>
              {serviceShortName}. {doctor.name} - {doctor.specialization}
            </div>

            <div className="mb-4">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <div className="d-flex align-items-center gap-2">
                  <Button variant="link" className="p-0 text-dark" onClick={handlePrevMonth}>
                    <BsChevronLeft />
                  </Button>
                  <span style={{ fontSize: '14px' }}>{months[currentMonth]} {currentYear}</span>
                  <Button variant="link" className="p-0 text-dark" onClick={handleNextMonth}>
                    <BsChevronRight />
                  </Button>
                </div>
              </div>

              <Row className="mb-2">
                {days.map((day, index) => (
                  <Col key={index} className="text-center p-0">
                    <small className="text-muted">{day}</small>
                  </Col>
                ))}
              </Row>

              {calendar.map((week, weekIndex) => (
                <Row key={weekIndex} className="mb-2">
                  {week.map((day, dayIndex) => {
                    if (!day) return <Col key={dayIndex} className="text-center p-0" />;

                    const formattedDay = String(day).padStart(2, '0');
                    const formattedMonth = String(currentMonth + 1).padStart(2, '0');
                    const fullDate = `${currentYear}-${formattedMonth}-${formattedDay}`;
                    const currentDate = new Date(fullDate);
                    const isPastDate = currentDate < today;
                    const isUnavailable = unavailableDates.includes(fullDate) && !isPastDate;
                    const isSelected = selectedDate === fullDate;
                    const isToday = !isSelected && 
                                  currentDate.getDate() === today.getDate() &&
                                  currentDate.getMonth() === today.getMonth() &&
                                  currentDate.getFullYear() === today.getFullYear();

                    return (
                      <Col key={dayIndex} className="text-center p-0">
                        <Button
                          variant={
                            isPastDate
                              ? 'outline-secondary'
                              : isUnavailable
                                ? 'outline-danger'
                                : isSelected
                                  ? 'danger'
                                  : 'light'
                          }
                          onClick={() => !isPastDate && handleDateClick(fullDate)}
                          className={`
                            rounded-circle p-0 
                            ${isPastDate ? 'text-muted' : ''}
                            ${isUnavailable ? 'text-danger' : ''}
                          `}
                          style={{
                            width: '36px',
                            height: '36px',
                            fontSize: '14px',
                            opacity: isPastDate ? 0.6 : 1,
                            borderWidth: isToday ? '2px' : '1px',
                            cursor: isPastDate ? 'not-allowed' : 'pointer',
                          }}
                          disabled={isPastDate}
                        >
                          {day}
                        </Button>
                      </Col>
                    );
                  })}
                </Row>
              ))}
            </div>

            {selectedDate && isDateUnavailable(selectedDate) ? (
              <div className="text-center mb-4">
                <p className="text-danger mb-2">Doctor not available</p>
                <p className="text-muted" style={{ fontSize: '14px' }}>
                  Please select another date.
                </p>
              </div>
            ) : (
              <>
                {isLoadingSlots ? (
                  <div className="text-center my-4">
                    <Spinner animation="border" role="status" variant="primary">
                      <span className="visually-hidden">Loading...</span>
                    </Spinner>
                    <p className="mt-2">Loading available slots...</p>
                  </div>
                ) : (
                  <>
                    {morningSlots.length > 0 && (
                      <>
                        <h6 className="mb-3">Morning Slots</h6>
                        <div className="d-flex flex-wrap gap-2 mb-4">
                          {morningSlots.map((slot, index) => (
                            <Button
                              key={index}
                              variant={selectedSlots.includes(slot) ? 'danger' : 'outline-secondary'}
                              onClick={() => handleSlotClick(slot)}
                              style={{ fontSize: '14px' }}
                              disabled={isDateUnavailable(selectedDate)}
                            >
                              {slot}
                            </Button>
                          ))}
                        </div>
                      </>
                    )}

                    {eveningSlots.length > 0 && (
                      <>
                        <h6 className="mb-3">Evening Slots</h6>
                        <div className="d-flex flex-wrap gap-2">
                          {eveningSlots.map((slot, index) => (
                            <Button
                              key={index}
                              variant={selectedSlots.includes(slot) ? 'danger' : 'outline-secondary'}
                              onClick={() => handleSlotClick(slot)}
                              style={{ fontSize: '14px' }}
                              disabled={isDateUnavailable(selectedDate)}
                            >
                              {slot}
                            </Button>
                          ))}
                        </div>
                      </>
                    )}

                    {!isLoadingSlots && morningSlots.length === 0 && eveningSlots.length === 0 && (
                      <div className="text-center mb-4">
                        <p className="text-muted">No available slots for this date</p>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <div className="d-flex gap-2 mt-4">
              <Button variant="light" className="flex-grow-1" onClick={onClose} style={{ fontSize: '14px' }}>
                Cancel
              </Button>
              <Button
                variant="danger"
                className="flex-grow-1"
                disabled={selectedSlots.length === 0 || isDateUnavailable(selectedDate) || isLoadingSlots}
                onClick={handleNext}
                style={{ fontSize: '14px' }}
              >
                Next
              </Button>
            </div>
          </>
        ) : (
          <PatientDetails
            doctor={doctor}
            selectedDate={selectedDate}
            selectedSlots={selectedSlots}
            fullDate={fullDate}
            onBack={handleBack}
            onSubmit={handleSubmit}
            serviceShortName={serviceShortName}
            serviceName={serviceName}
          />
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default BookAppointment;
